import React, { useState, useCallback } from 'react';
import { useNavigate, useAsyncValue, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../offline/useRevalidateOnSync';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components Styled
import {
  ContainerStyled,
  ContainerButtonStyled,
  ContainerButtonSmall,
} from './styles';

// Components
import { MainButton } from '../../../../components/inputs/buttons/MainButton';
import { CampaignTable } from '../../../../components/display/tables/CampaignTable';

// Types
import { formOrderNaming } from '../../../../components/forms/NewWorkOrderForm/constants';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';
import { dataTypes } from '../../../../components/display/tables/CampaignTable/types';

// GQL
import { client } from '../../../../gql/client';
import { GET_CAMPAIGNS } from '../../../../gql/queries/campaign';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { campaignIdTypes } from './types';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { store } from '../../../../state/configureStore';

// Hooks
import { usePollingRevalidation } from '../../../../hooks/usePollingRevalidation';

export default function CampaignsPage(): JSX.Element {
  const { t } = useTranslation('pages');
  const data = useAsyncValue() as dataTypes;
  const dispatch = useDispatch();
  const revalidator = useRevalidator();
  useRevalidateOnSync();

  const [campaignId, setCampaignId] = useState<campaignIdTypes>({});

  const navigate = useNavigate();

  const handleNewCampaign = () => {
    navigate('new-campaign');
  };

  const handleEditCampaign = () => {
    sessionStorage.removeItem(formOrderNaming.EDIT_CAMPAIGN_FORM);
    navigate(`edit-campaign/${campaignId?.id}`);
  };

  const handleShowCampaign = () => {
    navigate(`${campaignId?.id}`);
  };

  const onReFetchData = useCallback(async () => {
    // Always read from store.getState() — avoids stale closure
    const currentCursor = getCursor(tableIds.CAMPAIGNS)(store.getState());
    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    const { data } = await client.query({
      query: GET_CAMPAIGNS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: currentCursor.cursor ?? '',
          orderBy: currentCursor.orderBy ?? 'desc',
          sortField: currentCursor.sortField,
          name: currentCursor.name,
          fromDate: currentCursor.fromDate,
          toDate: currentCursor.toDate,
          concessionId: currentCursor.concessionId,
          predefinedOrderId: currentCursor.predefinedOrderId,
          states: currentCursor.states,
          userIds: currentCursor.userIds,
          teamIds: currentCursor.teamIds ?? [],
          track: currentCursor.track,
          pkInitKm: currentCursor.pkInitKm,
          pkInitMeter: currentCursor.pkInitMeter,
          pkEndKm: currentCursor.pkEndKm,
          pkEndMeter: currentCursor.pkEndMeter,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });

    const setCursorData = {
      take: effectiveTake,
      cursor: currentCursor.cursor ?? '',
      // OMIT sortField + orderBy: cursorTables merge preservará los del cursor activo
      name: currentCursor?.name,
      fromDate: currentCursor?.fromDate,
      toDate: currentCursor?.toDate,
      concessionId: currentCursor?.concessionId,
      predefinedOrderId: currentCursor?.predefinedOrderId,
      states: currentCursor?.states,
      userIds: currentCursor?.userIds,
      teamIds: currentCursor?.teamIds ?? [],
      track: currentCursor?.track,
      pkInitKm: currentCursor?.pkInitKm,
      pkInitMeter: currentCursor?.pkInitMeter,
      pkEndKm: currentCursor?.pkEndKm,
      pkEndMeter: currentCursor?.pkEndMeter,
      onlyFlagged: currentCursor?.onlyFlagged,
    };

    dispatch(setCursor(tableIds.CAMPAIGNS, setCursorData));
    revalidator.revalidate();
  }, []);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, []);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(tableIds.CAMPAIGNS)(store.getState());
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    await client.query({
      query: GET_CAMPAIGNS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          orderBy: currentCursor.orderBy ?? 'desc',
          sortField: currentCursor.sortField,
          name: currentCursor.name,
          fromDate: currentCursor.fromDate,
          toDate: currentCursor.toDate,
          concessionId: currentCursor.concessionId,
          predefinedOrderId: currentCursor.predefinedOrderId,
          states: currentCursor.states,
          userIds: currentCursor.userIds,
          teamIds: currentCursor.teamIds ?? [],
          track: currentCursor.track,
          pkInitKm: currentCursor.pkInitKm,
          pkInitMeter: currentCursor.pkInitMeter,
          pkEndKm: currentCursor.pkEndKm,
          pkEndMeter: currentCursor.pkEndMeter,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, []);
  usePollingRevalidation(onPollingRefresh);

  return (
    <ContainerStyled>
      <CampaignTable
        titleText={t('campaigns.filterBy')}
        setRowState={setCampaignId}
        data={data}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={tableIds.CAMPAIGNS}
        showFlagColumn
        showOnlyFlaggedFilter
      />
      <ContainerButtonStyled>
        <MainButton
          variant="secondary"
          text={t('campaigns.new')}
          onClick={handleNewCampaign}
        />
        <ContainerButtonSmall>
          <MainButton
            variant="secondary"
            text={t('campaigns.edit')}
            onClick={handleEditCampaign}
            disabled={!campaignId.id}
          />
          <MainButton
            text={t('campaigns.view')}
            onClick={handleShowCampaign}
            disabled={!campaignId.id}
          />
        </ContainerButtonSmall>
      </ContainerButtonStyled>
    </ContainerStyled>
  );
}
